// 
/* ============================================================
   NEAT EVERYDAY — EB (Easy Bundle Builder) → GoKwik Sidecart
   Groups bundle line items into one locked card in the sidecart.
   - Real line items are untouched (GST Pro / checkout unaffected)
   - Grouping key = properties.Bundle + properties["Box ID"]
   - Price / quantity stepper / remove button now mirror GoKwik's
     native svelte markup (item-price / quantity-handler-wrapper /
     quantity-handler / reduce-quantity / item-quantity /
     increase-quantity / remove-btn) so the native CSS picks them up.
   - Component thumbnails render in a single row (4-up).
   - A "box" is valid whenever 2+ total UNITS carry the same
     Bundle + Box ID combo — whether that's 2+ different products
     (separate line items) or the SAME product picked multiple times
     (Shopify merges same variant + same properties into ONE line
     item with quantity > 1). Both cases now collapse into the card.
   - Native tiles that GoKwik itself periodically re-renders on its
     own (e.g. a qty>1 line's "-N+" stepper) no longer cause the
     card to flicker back to the raw tile: the grouping is reapplied
     from cached data the instant that mutation is observed, before
     the slower network-backed reconciliation runs.
   Paste this inside a <script> tag in theme.liquid, right before </body>.
   ============================================================ */

(function () {
  "use strict";

  // ---- 1. CONFIG ---------------------------------------------------
  var BUNDLE_DISPLAY_NAMES = {
    "SoftGels-Blister-10": { name: "Trial Pack", image: null }
  };

  var DEBOUNCE_MS = 150;
  var BUNDLE_CARD_CLASS = "neat-bundle-card";
  var HIDDEN_ATTR = "data-neat-bundle-hidden";
  var SHADOW_HOST_TAG = "shadow-dom-container";
  var SIDECART_MARKER = ".side-cart-header";

  // GoKwik only ever renders ONE ".next-milestone-text" node in the DOM —
  // whichever reward it (sometimes incorrectly) decides is "next" — so the
  // sentence for any reward the customer hasn't reached yet isn't available
  // to read from the page. These templates mirror the "Text Before Hitting
  // The Goal" field for each reward in the GoKwik admin panel, keyed by
  // that reward's Spend Goal in rupees, so the correct sentence can be
  // rebuilt for whichever milestone is actually next. "{amount}" is
  // replaced with the live remaining-amount figure.
  // ⚠️ If rewards are added/removed/edited in the GoKwik admin panel, this
  // map must be updated to match, or an edited/new reward will simply be
  // skipped (left as whatever GoKwik itself renders) rather than guessed.
  var MILESTONE_TEXT_TEMPLATES = {
    1000: "Add {amount} more to unlock a FREE Softgel (Worth up to ₹242)!",
    1500: "Add {amount} more to pick FREE Castor Oil or Aloe Vera (Worth up to ₹299)!",
    2000: "Add {amount} more to get FREE Rose Water (Worth ₹450)!",
    3000: "Add {amount} more to unlock Rose Water + Aloe Vera FREE (Worth ₹645)!"
  };

  // Shown in place of an "Add ₹X more..." sentence once the cart total has
  // cleared the highest reward's goal (₹3000) — mirrors the highest
  // reward's own unlocked-state copy.
  var MILESTONE_ALL_UNLOCKED_TEXT =
    "Congrats! You have unlocked Rose Water(50ml) + AloeVera (Tube-50g) as a free gift!";

  // ---- 2. STATE ------------------------------------------------------
  var cartItemsCache = null;
  var cartTotalPaiseCache = null;
  var cartFetchInFlight = null;
  var renderScheduled = false;
  var observedRoot = null;

  // ---- 3. HELPERS ------------------------------------------------------
  function formatINR(paise) {
    return "₹" + (paise / 100).toFixed(2);
  }

  // Sum of each line item's discounted price. Verified against GoKwik's
  // own rendering: with a single ₹419 (discounted) item in cart, GoKwik's
  // progress-bar-fill width (10.47%, i.e. 419/1000 scaled into the 0–25%
  // segment) and its "Add ₹581.00 more..." text (1000 − 419) both key off
  // this figure — NOT cart.total_price (₹469 on that same cart, which
  // includes non-item charges like COD fees and doesn't match either of
  // GoKwik's own numbers). Using items_subtotal_price / this sum instead
  // of total_price is what keeps our recomputed text consistent with
  // GoKwik's own math rather than a different, larger total.
  function itemsSubtotalPaise(cart) {
    if (!cart || !cart.items) return null;
    if (typeof cart.items_subtotal_price === "number") return cart.items_subtotal_price;
    return cart.items.reduce(function (sum, item) {
      return sum + (item.final_line_price != null ? item.final_line_price : (item.line_price || 0));
    }, 0);
  }

  function fetchCart(useCache) {
    if (useCache && cartItemsCache) return Promise.resolve(cartItemsCache);
    if (cartFetchInFlight) return cartFetchInFlight;
    cartFetchInFlight = fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var map = {};
        cart.items.forEach(function (item) { map[item.key] = item; });
        cartItemsCache = map;
        var sub = itemsSubtotalPaise(cart);
        if (sub != null) cartTotalPaiseCache = sub;
        cartFetchInFlight = null;
        return map;
      })
      .catch(function (err) {
        cartFetchInFlight = null;
        console.error("[neat-bundle] cart.js fetch failed", err);
        return cartItemsCache || {};
      });
    return cartFetchInFlight;
  }

  function mapTilesToCartItems(tiles, cartMap) {
    var cartArr = Object.keys(cartMap).map(function (k) { return cartMap[k]; });
    var pairs = [];
    tiles.forEach(function (tile, i) {
      var cartItem = cartArr[i];
      if (!cartItem) return;
      var titleEl = tile.querySelector(".sidecart-product-title");
      var titleText = titleEl ? titleEl.textContent.trim() : "";
      if (titleText && cartItem.title && titleText !== cartItem.title.trim()) return;
      pairs.push({ tile: tile, item: cartItem });
    });
    return pairs;
  }

  function groupByBundle(pairs) {
    var groups = {};
    pairs.forEach(function (pair) {
      var props = pair.item.properties || {};
      var bundleKey = props["Bundle"];
      var boxId = props["Box ID"];
      if (!bundleKey || boxId === undefined || boxId === null) return;
      var groupKey = bundleKey + "::" + boxId;
      if (!groups[groupKey]) {
        groups[groupKey] = { bundleKey: bundleKey, boxId: boxId, pairs: [] };
      }
      groups[groupKey].pairs.push(pair);
    });
    return groups;
  }

  function changeBundleQuantity(keys, currentQty, delta, container) {
    var newQty = currentQty + delta;
    if (newQty < 1) return;
    if (container) container.style.opacity = "0.6";

    var updates = {};
    keys.forEach(function (key) { updates[key] = newQty; });

    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        cartItemsCache = null;
        document.dispatchEvent(new CustomEvent("cart:updated"));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      })
      .catch(function (err) {
        console.error("[neat-bundle] failed to change bundle quantity", err);
        if (container) container.style.opacity = "1";
      });
  }

  // Parse a rendered currency string like "₹1,452.00" back to paise.
  // Returns null (instead of 0) if it can't confidently parse, so callers
  // can choose to leave the element untouched rather than risk writing a
  // wrong value into it.
  function parseCurrencyToPaise(text) {
    if (!text) return null;
    var digits = text.replace(/[^\d.]/g, "");
    if (!digits) return null;
    var val = parseFloat(digits);
    if (isNaN(val)) return null;
    return Math.round(val * 100);
  }

  // Subtract the removed bundle's own price contribution from whatever the
  // Estimated Total block currently shows. This only ever rewrites the
  // existing text of the existing elements — it never adds, removes, or
  // restyles anything, and never blanks the strike-through/%-off unless
  // they're already gone from the DOM. Deliberately independent of the
  // server response so it applies the instant the button is clicked.
  function adjustEstimatedTotal(deltaFinalPaise, deltaStrikePaise) {
    var root = findSidecartRoot();
    if (!root) return;
    var container = root.querySelector(".estimated-total-container");
    if (!container) return;

    var strikeEl = container.querySelector("strike");
    var discountEl = container.querySelector(".discount-percentage");
    var finalEl = null;
    var spans = container.querySelectorAll("span");
    for (var i = 0; i < spans.length; i++) {
      if (!spans[i].classList.contains("discount-percentage")) { finalEl = spans[i]; break; }
    }
    if (!finalEl) return;

    var curFinal = parseCurrencyToPaise(finalEl.textContent);
    if (curFinal == null) return;
    var newFinal = Math.max(0, curFinal - deltaFinalPaise);
    finalEl.textContent = formatINR(newFinal);

    if (strikeEl && strikeEl.textContent) {
      var curStrike = parseCurrencyToPaise(strikeEl.textContent);
      if (curStrike != null) {
        var newStrike = Math.max(0, curStrike - deltaStrikePaise);
        strikeEl.textContent = formatINR(newStrike);
        if (discountEl && newStrike > 0) {
          var pct = Math.round((1 - newFinal / newStrike) * 100);
          discountEl.textContent = "(" + pct + "% OFF)";
        }
      }
    }
    // If there was no strike element to begin with, leave it exactly as
    // is — this store's total had no discount showing, so there's nothing
    // to adjust and nothing to risk breaking.
  }

  // Same subtraction approach for the "₹X Saved so far!" ribbon.
  function adjustSavedRibbon(deltaSavedPaise) {
    var root = findSidecartRoot();
    if (!root) return;
    var ribbon = root.querySelector(".footer-ribbon-text");
    if (!ribbon || !ribbon.textContent) return;
    var curSaved = parseCurrencyToPaise(ribbon.textContent);
    if (curSaved == null) return;
    var newSaved = Math.max(0, curSaved - deltaSavedPaise);
    ribbon.textContent = newSaved > 0 ? (formatINR(newSaved) + " Saved so far!") : "";
  }

  // Directly patch the "Your Cart (N items)" header by subtracting the
  // number of units the removed bundle contributed, rather than waiting
  // for the server response.
  function adjustHeaderItemCount(deltaCount) {
    var root = findSidecartRoot();
    if (!root) return;
    var header = root.querySelector(SIDECART_MARKER);
    if (!header) return;
    var walker = document.createTreeWalker(header, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      var match = node.textContent.match(/\(\s*(\d+)\s*item[s]?\s*\)/i);
      if (match) {
        var newCount = Math.max(0, parseInt(match[1], 10) - deltaCount);
        node.textContent = node.textContent.replace(
          /\(\s*\d+\s*item[s]?\s*\)/i,
          "(" + newCount + " " + (newCount === 1 ? "item" : "items") + ")"
        );
        break;
      }
    }
  }

  // Instant milestone progress-bar adjustment. We don't know GoKwik's
  // exact formula for what counts toward the milestone total (subtotal vs
  // discounted total, etc.), so rather than guess, we back it out from
  // GoKwik's own last-rendered fill width against the visible goal amounts
  // — that keeps us self-consistent with whatever GoKwik already drew,
  // instead of introducing a second, possibly-conflicting calculation.
  // Only inline style (border color, fill width) and SVG stroke/fill
  // attributes are touched — no elements added/removed, no text changed.
  // Build the list of (tick position %, goal amount) breakpoints from the
  // DOM, plus an implicit (0%, ₹0) starting point.
  function getMilestoneBreakpoints(stepsWraps) {
    var points = [{ pct: 0, goalPaise: 0 }];
    stepsWraps.forEach(function (step) {
      var leftMatch = (step.style.left || "0%").match(/([\d.]+)%/);
      var pct = leftMatch ? parseFloat(leftMatch[1]) : null;
      var goalEl = step.querySelector(".milestone-goal");
      var goalPaise = goalEl ? parseCurrencyToPaise(goalEl.textContent) : null;
      if (pct != null && goalPaise != null) points.push({ pct: pct, goalPaise: goalPaise });
    });
    points.sort(function (a, b) { return a.pct - b.pct; });
    return points;
  }

  // Inverse of widthFromImpliedTotal: given a rendered width %, interpolate
  // back to the rupee amount that would have produced it. This recovers
  // "what total did GoKwik's last render imply" without needing their
  // internal total value directly.
  function impliedTotalFromWidth(points, widthPct) {
    for (var i = 1; i < points.length; i++) {
      var p0 = points[i - 1], p1 = points[i];
      if (widthPct <= p1.pct || i === points.length - 1) {
        if (p1.pct === p0.pct) return p0.goalPaise;
        var frac = Math.max(0, Math.min(1, (widthPct - p0.pct) / (p1.pct - p0.pct)));
        return p0.goalPaise + frac * (p1.goalPaise - p0.goalPaise);
      }
    }
    return points[points.length - 1].goalPaise;
  }

  // Given a rupee amount, interpolate the width % between whichever two
  // consecutive tick marks it falls between. This is what keeps the bar
  // passing exactly through each circle at its tick position, in order —
  // ₹1,000 always lands at 25%, ₹1,500 always at 50%, etc. — instead of
  // scaling linearly against only the final (₹3,000) goal, which put the
  // bar in the wrong spot relative to the circles whenever the rupee gaps
  // between goals weren't equal.
  function widthFromImpliedTotal(points, totalPaise) {
    if (totalPaise <= points[0].goalPaise) return points[0].pct;
    for (var i = 1; i < points.length; i++) {
      var p0 = points[i - 1], p1 = points[i];
      if (totalPaise <= p1.goalPaise || i === points.length - 1) {
        if (p1.goalPaise === p0.goalPaise) return p1.pct;
        var frac = Math.max(0, Math.min(1, (totalPaise - p0.goalPaise) / (p1.goalPaise - p0.goalPaise)));
        return p0.pct + frac * (p1.pct - p0.pct);
      }
    }
    return points[points.length - 1].pct;
  }

  function adjustMilestoneProgress(deltaPaise) {
    var root = findSidecartRoot();
    if (!root) return;
    var progressContainer = root.querySelector(".progress-container");
    if (!progressContainer) return;
    var fillEl = progressContainer.querySelector(".progress-bar-fill");
    var stepsWraps = Array.prototype.slice.call(progressContainer.querySelectorAll(".steps-wrap"));
    if (!fillEl || !stepsWraps.length) return;

    var points = getMilestoneBreakpoints(stepsWraps);
    if (points.length < 2) return;

    var curWidthMatch = (fillEl.style.width || "0%").match(/([\d.]+)%/);
    var curWidthPct = curWidthMatch ? parseFloat(curWidthMatch[1]) : 0;
    var impliedTotal = impliedTotalFromWidth(points, curWidthPct);
    var newImpliedTotal = Math.max(0, impliedTotal - deltaPaise);
    var newWidthPct = widthFromImpliedTotal(points, newImpliedTotal);
    fillEl.style.width = newWidthPct.toFixed(2) + "%";

    stepsWraps.forEach(function (step) {
      var goalEl = step.querySelector(".milestone-goal");
      var goalPaise = goalEl ? parseCurrencyToPaise(goalEl.textContent) : null;
      if (goalPaise == null) return;
      var achieved = newImpliedTotal >= goalPaise;
      var color = achieved ? "#15b79e" : "#d60f0f";

      step.style.border = "1px solid " + color;
      var iconWrap = step.querySelector(".icon-wrap");
      if (!iconWrap) return;
      iconWrap.style.border = "1px solid " + color;
      var svg = iconWrap.querySelector("svg");
      if (!svg) return;
      svg.querySelectorAll("path[stroke]").forEach(function (p) {
        p.setAttribute("stroke", color);
      });
      var clipRect = svg.querySelector("clipPath rect");
      if (clipRect) clipRect.setAttribute("fill", achieved ? "#E0F7F4" : "none");
    });
  }

  // Recompute the milestone sentence from scratch, rather than nudging
  // whatever GoKwik already rendered. GoKwik renders exactly one
  // ".next-milestone-text" node and can pick the wrong reward for it (e.g.
  // showing the ₹3000 reward's sentence while the cart is still under
  // ₹1000) — patching that existing (wrong) text would just carry the
  // mistake forward. Instead: read each reward's goal amount straight off
  // its own milestone circle (".milestone-goal"), find the first goal the
  // current cart total hasn't reached yet, and render that reward's own
  // template from MILESTONE_TEXT_TEMPLATES. Once every goal has been
  // reached, MILESTONE_ALL_UNLOCKED_TEXT is shown instead.
  //
  // cartTotalPaise should be the actual current cart total (from
  // cart.js's items subtotal — see itemsSubtotalPaise()), not inferred
  // from the progress-bar width or from the old text — those are exactly
  // what's unreliable here.
  //
  // If the next unreached goal isn't in MILESTONE_TEXT_TEMPLATES (a reward
  // was added/edited in the admin panel and this map wasn't updated), this
  // intentionally leaves whatever GoKwik already rendered untouched rather
  // than guessing.
  function fixMilestoneText(cartTotalPaise) {
    if (typeof cartTotalPaise !== "number") return;
    var root = findSidecartRoot();
    if (!root) return;
    var progressContainer = root.querySelector(".progress-container");
    if (!progressContainer) return;
    var textEl = progressContainer.querySelector(".next-milestone-text");
    if (!textEl) return;
    var stepsWraps = Array.prototype.slice.call(progressContainer.querySelectorAll(".steps-wrap"));
    if (!stepsWraps.length) return;

    var goalsPaise = [];
    stepsWraps.forEach(function (step) {
      var goalEl = step.querySelector(".milestone-goal");
      var goalPaise = goalEl ? parseCurrencyToPaise(goalEl.textContent) : null;
      if (goalPaise != null) goalsPaise.push(goalPaise);
    });
    goalsPaise.sort(function (a, b) { return a - b; });
    if (!goalsPaise.length) return;

    var nextGoalPaise = null;
    for (var i = 0; i < goalsPaise.length; i++) {
      if (cartTotalPaise < goalsPaise[i]) { nextGoalPaise = goalsPaise[i]; break; }
    }

    if (nextGoalPaise == null) {
      // Cart total has cleared every goal, including the highest (₹3000).
      textEl.textContent = MILESTONE_ALL_UNLOCKED_TEXT;
      return;
    }

    var goalRupees = Math.round(nextGoalPaise / 100);
    var template = MILESTONE_TEXT_TEMPLATES[goalRupees];
    if (!template) return; // unrecognised reward — don't guess its wording

    var amountLeftPaise = Math.max(0, nextGoalPaise - cartTotalPaise);
    textEl.textContent = template.replace("{amount}", formatINR(amountLeftPaise));
  }

  function removeBundle(keys, triggerEl) {
    if (triggerEl) {
      triggerEl.style.pointerEvents = "none";
      triggerEl.style.opacity = "0.5";
    }
    var updates = {};
    keys.forEach(function (key) { updates[key] = 0; });

    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Special case: if this bundle was the only thing in the cart,
        // GoKwik's header count / free-gift banner / milestone bar / total
        // have nothing in-place to sync against and can sit stale for a
        // long time — showing "Your Cart (11 items)" with an empty list
        // reads as a broken site. A reload is the one reliable way to get
        // GoKwik's own components into the correct "Nothing here / Explore
        // products" state immediately. This does close and reopen the
        // drawer, but only for the empty-cart case.
        if (cart && cart.item_count === 0) {
          window.location.reload();
          return;
        }

        // Otherwise (other products remain): stay in place, no reload.
        // The response already contains the fully up-to-date cart, so use
        // it directly instead of firing another /cart.js request. Re-run
        // our grouping logic immediately (useCache=true, no network wait)
        // so any leftover hidden tiles / bundle cards for this bundle are
        // cleaned up in the same tick — no window where a raw, ungrouped
        // "Blister 10" tile with its own qty/delete controls could flash
        // into view.
        var map = {};
        cart.items.forEach(function (item) { map[item.key] = item; });
        cartItemsCache = map;
        var sub = itemsSubtotalPaise(cart);
        if (sub != null) cartTotalPaiseCache = sub;
        render(true);

        // Best-effort nudge for GoKwik's own header/free-gift-banner/
        // milestone-bar/totals chrome to refresh in the background.
        document.dispatchEvent(new CustomEvent("cart:updated"));
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      })
      .catch(function (err) {
        console.error("[neat-bundle] failed to remove bundle", err);
        if (triggerEl) {
          triggerEl.style.pointerEvents = "auto";
          triggerEl.style.opacity = "1";
        }
      });
  }

  // Exact SVG markup GoKwik uses for its native remove button, so the icon
  // and native CSS rules (iconify classes) match perfectly.
  var TRASH_SVG_NATIVE =
    '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" width="16px" height="16px" viewBox="0 0 24 24" class="iconify iconify--material-symbols">' +
    '<path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zm2-4h2V8H9zm4 0h2V8h-2z"></path></svg>';

  // Green checkmark shown briefly in place of the trash icon to confirm
  // the removal registered, before the card collapses away.
  var CHECK_SVG_CONFIRM =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24">' +
    '<path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path></svg>';

  function buildBundleCard(group) {
    var cfg = BUNDLE_DISPLAY_NAMES[group.bundleKey] || {};
    var displayName = (typeof cfg === "string") ? cfg : (cfg.name || group.bundleKey);
    var customImage = (typeof cfg === "object" && cfg.image) ? cfg.image : null;

    var nowPaise = group.pairs.reduce(function (sum, p) { return sum + (p.item.final_line_price || p.item.line_price || 0); }, 0);
    var wasPaise = group.pairs.reduce(function (sum, p) { return sum + (p.item.original_line_price || p.item.line_price || 0); }, 0);
    var hasDiscount = wasPaise > nowPaise;
    var pctOff = hasDiscount ? Math.round((1 - nowPaise / wasPaise) * 100) : 0;

    var qty = group.pairs[0].item.quantity;
    var keys = group.pairs.map(function (p) { return p.item.key; });
    var componentTiles = group.pairs.map(function (p) { return p.tile; });

    var card = document.createElement("div");
    card.className = BUNDLE_CARD_CLASS;
    card.setAttribute("data-bundle-key", group.bundleKey);
    card.setAttribute("data-box-id", group.boxId);
    card.style.cssText = [
      "display:flex", "gap:12px", "align-items:center", "text-align:center",
      "background:#ffffff", "border:1px solid #e1e3e5",
      "border-radius:10px", "padding:12px", "margin-bottom:10px",
      "box-shadow:0 1px 3px rgba(0,0,0,0.06)"
    ].join(";");

    // ---- Thumbnails: all in a single row (no wrap) ----
    var imgWrap = document.createElement("div");
    if (customImage) {
      imgWrap.style.cssText = "flex-shrink:0;display:flex;justify-content:center;transition:opacity 0.15s ease;";
      var heroImg = document.createElement("img");
      heroImg.src = customImage;
      heroImg.alt = displayName;
      heroImg.style.cssText = "width:60px;height:60px;border-radius:8px;object-fit:cover;";
      imgWrap.appendChild(heroImg);
    } else {
      imgWrap.style.cssText = "display:flex;flex-wrap:wrap;gap:3px;flex-shrink:0;justify-content:center;align-items:center;width:97px;";
      group.pairs.forEach(function (p) {
        var thumb = document.createElement("img");
        thumb.src = p.item.image;
        thumb.alt = p.item.title;
        thumb.style.cssText = "width:22px;height:22px;border-radius:5px;object-fit:cover;border:1px solid #f0f0f0;flex-shrink:0;transition:opacity 0.2s ease, transform 0.2s ease;";
        imgWrap.appendChild(thumb);
      });
    }
    card.appendChild(imgWrap);

    // ---- Title ----
    var details = document.createElement("div");
    details.style.cssText = "flex:1;min-width:0;text-align:center;";
    details.innerHTML = '<div style="font-weight:600;color:#0b1f3a;font-size:14px;line-height:1.3;text-align:center;">' + displayName + '</div>';
    card.appendChild(details);

    // ---- Price + quantity stepper + remove ----
    // Native GoKwik scoped CSS (svelte-10zz03f) doesn't cascade onto elements
    // we insert outside its own component tree, so styling is applied
    // explicitly here to visually match the native pill stepper + blue
    // remove button.
    var itemPrice = document.createElement("div");
    itemPrice.className = "item-price svelte-10zz03f";
    itemPrice.style.cssText = "display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;flex-shrink:0;";

    var priceSpan = document.createElement("span");
    priceSpan.className = "svelte-10zz03f";
    priceSpan.style.cssText = "font-weight:700;color:#0b1f3a;font-size:14px;text-align:center;white-space:nowrap;";
    if (hasDiscount) {
      priceSpan.innerHTML =
        '<span style="text-decoration:line-through;color:#8c9196;font-size:11px;margin-right:4px;font-weight:400;">' + formatINR(wasPaise) + '</span>' +
        formatINR(nowPaise) +
        '<br><span style="color:#15b79e;font-weight:600;font-size:10px;">(' + pctOff + '% OFF)</span>';
    } else {
      priceSpan.textContent = formatINR(nowPaise);
    }
    itemPrice.appendChild(priceSpan);

    var qtyWrapper = document.createElement("div");
    qtyWrapper.className = "quantity-handler-wrapper svelte-10zz03f";
    qtyWrapper.style.cssText = "display:flex;align-items:center;justify-content:center;gap:10px;";

    var removeBtn = document.createElement("div");
    removeBtn.className = "remove-btn svelte-10zz03f";
    removeBtn.style.cssText = "display:flex;align-items:center;justify-content:center;color:#0b1f3a;cursor:pointer;flex-shrink:0;margin-left:2px;";
    removeBtn.innerHTML = TRASH_SVG_NATIVE;
    removeBtn.addEventListener("click", function () {
      // Guard against double-clicks firing a second removal.
      if (removeBtn.getAttribute("data-removing") === "1") return;
      removeBtn.setAttribute("data-removing", "1");
      removeBtn.style.pointerEvents = "none";

      // Step -1 — instant, synchronous header count + total adjustment.
      // We already know exactly what this bundle contributes (nowPaise /
      // wasPaise / qty are the very numbers shown on its own card), so we
      // don't need to wait for the server or re-derive anything from the
      // full cart — just subtract this bundle's share from whatever the
      // header/total currently show. Only text content is edited; the
      // existing structure and styling are left completely alone.
      adjustHeaderItemCount(keys.length * qty);
      adjustEstimatedTotal(nowPaise, wasPaise);
      adjustSavedRibbon(wasPaise - nowPaise);
      adjustMilestoneProgress(nowPaise);

      // Optimistic milestone-text fix: subtract this bundle's price from
      // whatever total we last fetched and recompute immediately, so the
      // sentence is right straight away rather than waiting on the network
      // round trip. removeBundle()'s response handler below re-syncs
      // cartTotalPaiseCache to the server's real total once it lands.
      if (typeof cartTotalPaiseCache === "number") {
        cartTotalPaiseCache = Math.max(0, cartTotalPaiseCache - nowPaise);
        fixMilestoneText(cartTotalPaiseCache);
      }

      // Step 0 — visually remove the component thumbnails ONE AT A TIME
      // (staggered), so it reads as "each product is being removed" even
      // though underneath it is always a single /cart/update.js call for
      // the whole bundle — never a separate request per product. This is
      // purely a perceived-progress animation, not real per-item work.
      var thumbs = Array.prototype.slice.call(imgWrap.querySelectorAll("img"));
      var STAGGER_MS = 180;
      var THUMB_FADE_MS = 220;
      thumbs.forEach(function (img, idx) {
        setTimeout(function () {
          img.style.opacity = "0";
          img.style.transform = "scale(0.6)";
        }, idx * STAGGER_MS);
      });
      var allThumbsDoneAt = thumbs.length
        ? (thumbs.length - 1) * STAGGER_MS + THUMB_FADE_MS
        : 0;

      // Kick off the real (single, whole-bundle) cart removal in the
      // background right away — it does not wait for the thumbnail
      // animation. The animation just gives the customer something to
      // watch while it completes.
      removeBundle(keys, removeBtn);

      // Permanently delete the underlying (already-hidden) component tiles
      // from the DOM right now, rather than leaving them display:none for
      // render() to reconcile later. render()'s reset step unhides *all*
      // previously-hidden tiles before re-pairing them against fresh cart
      // data — if the DOM hasn't caught up yet, tiles for items we just
      // removed can briefly show with their raw native qty/delete controls.
      // Deleting them outright here closes that window entirely.
      componentTiles.forEach(function (t) {
        if (t && t.parentNode) t.remove();
      });

      // Step 1 — once the last thumbnail has finished fading, confirm on
      // the price/icon side: swap the trash icon for a green checkmark
      // and strike through the price.
      setTimeout(function () {
        removeBtn.style.transition = "transform 0.2s ease, color 0.2s ease";
        removeBtn.style.color = "#15b160";
        removeBtn.style.transform = "scale(1.2)";
        removeBtn.innerHTML = CHECK_SVG_CONFIRM;

        priceSpan.style.transition = "opacity 0.25s ease";
        priceSpan.style.textDecoration = "line-through";
        priceSpan.style.opacity = "0.45";
      }, allThumbsDoneAt);

      // Step 2 — after a beat long enough for the checkmark to register,
      // collapse and remove the whole card.
      setTimeout(function () {
        card.style.pointerEvents = "none";
        card.style.transition = "opacity 0.15s ease, max-height 0.2s ease, margin 0.2s ease, padding 0.2s ease";
        card.style.opacity = "0";
        card.style.overflow = "hidden";
        card.style.maxHeight = card.offsetHeight + "px";
        // Force a reflow so the max-height transition actually animates from
        // the current height down to 0 instead of jumping instantly.
        void card.offsetHeight;
        card.style.maxHeight = "0px";
        card.style.marginBottom = "0px";
        card.style.paddingTop = "0px";
        card.style.paddingBottom = "0px";
        setTimeout(function () {
          if (card.parentNode) card.remove();
        }, 200);
      }, allThumbsDoneAt + 350);
    });
    qtyWrapper.appendChild(removeBtn);

    itemPrice.appendChild(qtyWrapper);
    card.appendChild(itemPrice);

    return card;
  }

  function findSidecartRoot() {
    var hosts = document.querySelectorAll(SHADOW_HOST_TAG);
    for (var i = 0; i < hosts.length; i++) {
      var root = hosts[i].shadowRoot;
      if (root && root.querySelector(SIDECART_MARKER)) return root;
    }
    return null;
  }

  // ---- 4. MAIN RENDER --------------------------------------------------
  function render(useCache) {
    var root = findSidecartRoot();
    if (!root) return;

    var list = root.querySelector(".item-list-wrapper");
    if (!list) return;

    fetchCart(useCache).then(function (cartMap) {
      var freshRoot = findSidecartRoot();
      if (!freshRoot) return;
      var freshList = freshRoot.querySelector(".item-list-wrapper");
      if (!freshList) return;

      innerObserver.disconnect();

      freshList.querySelectorAll("." + BUNDLE_CARD_CLASS).forEach(function (el) { el.remove(); });
      freshList.querySelectorAll("[" + HIDDEN_ATTR + "]").forEach(function (el) {
        el.style.display = "";
        el.removeAttribute(HIDDEN_ATTR);
      });

      var currentTiles = Array.prototype.slice.call(
        freshList.querySelectorAll(".product-tile-wrapper")
      );
      var pairs = mapTilesToCartItems(currentTiles, cartMap);
      var groups = groupByBundle(pairs);

      Object.keys(groups).forEach(function (groupKey) {
        var group = groups[groupKey];

        // A valid box exists whenever there are 2+ total UNITS carrying
        // this Bundle + Box ID combo — whether that's 2+ different
        // products (2+ separate line items) or the SAME product picked
        // multiple times, which Shopify merges into ONE line item with
        // quantity > 1. The old check (`group.pairs.length < 2`) only
        // counted distinct line items, so a single-product / qty-3 box
        // fell through and stayed as a native tile with its own "-3+"
        // stepper.
        var totalUnits = group.pairs.reduce(function (sum, p) {
          return sum + p.item.quantity;
        }, 0);
        if (totalUnits < 2) return;

        var firstTile = group.pairs[0].tile;
        var card = buildBundleCard(group);
        firstTile.parentNode.insertBefore(card, firstTile);

        group.pairs.forEach(function (p) {
          p.tile.style.display = "none";
          p.tile.setAttribute(HIDDEN_ATTR, "1");
        });
      });

      fixMilestoneText(cartTotalPaiseCache);

      if (freshRoot) innerObserver.observe(freshRoot, { childList: true, subtree: true });
    });
  }

  function scheduleRender() {
    if (renderScheduled) return;
    renderScheduled = true;
    setTimeout(function () {
      renderScheduled = false;
      render();
    }, DEBOUNCE_MS);
  }

  // ---- 5. WATCH FOR SIDECART CHANGES -----------------------------------
  var innerObserver = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (m) {
      return !(m.target && (
        m.target.classList && m.target.classList.contains(BUNDLE_CARD_CLASS)
      ));
    });
    if (!relevant) return;

    // GoKwik periodically re-renders a native tile on its own (e.g. the
    // "-N+" stepper for a qty>1 line item), recreating the DOM node and
    // wiping our hidden-tile state — with no real cart change involved.
    // Re-applying the grouping from CACHED data first closes that
    // visibility gap almost instantly, so the card no longer flickers
    // back to the raw tile before collapsing again. scheduleRender()
    // still runs afterward as a debounced, network-backed reconciliation
    // in case something actually did change.
    render(true);
    scheduleRender();
  });

  var outerObserver = new MutationObserver(function () {
    attachToShadowRootIfNeeded();
  });

  function injectStyleOnce(root) {
    if (root.querySelector("#neat-bundle-style")) return;
    var style = document.createElement("style");
    style.id = "neat-bundle-style";
    style.textContent =
      ".neat-qty-input::-webkit-outer-spin-button," +
      ".neat-qty-input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}" +
      ".neat-qty-input{-moz-appearance:textfield;}";
    root.appendChild(style);
  }

  function attachToShadowRootIfNeeded() {
    var root = findSidecartRoot();
    if (root && root !== observedRoot) {
      observedRoot = root;
      injectStyleOnce(root);
      innerObserver.disconnect();
      innerObserver.observe(root, { childList: true, subtree: true });
      scheduleRender();
    }
  }

  function init() {
    outerObserver.observe(document.body, { childList: true, subtree: true });
    attachToShadowRootIfNeeded();
    var attempts = 0;
    var poll = setInterval(function () {
      attachToShadowRootIfNeeded();
      if (++attempts > 40 || observedRoot) clearInterval(poll);
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();